import React, { useState, useContext, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Avatar,
  CardMedia,
  CircularProgress,
  IconButton,
  TextField,
  Stack,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import ImageCropper from "../components/ImageCropper";
import ResponsiveTopBar from "../components/ResponsiveTopBar";
import MobileNavDrawer from "../components/MobileNavDrawer";
import { DeviceContext } from "../context/DeviceContext";
import { getImageUrl } from "../utils/imageUrl";

const DL = {
  bgDark: "#0f1419",
  bgMedium: "#1a1f26",
  bgLight: "#242930",
  green: "#62f481",
  greenEmerald: "#44aa5a",
  textPrimary: "#f9f9cf",
  textSecondary: "#99a1af",
  textMuted: "#6a7282",
  border: "rgba(255,255,255,0.08)",
  borderSubtle: "rgba(255,255,255,0.15)",
  borderGreen: "rgba(98,244,129,0.3)",
  divider: "rgba(98,244,129,0.2)",
  red: "#ff6467",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: DL.bgLight,
    borderRadius: "8px",
    "& fieldset": { borderColor: DL.border },
    "&:hover fieldset": { borderColor: DL.borderSubtle },
    "&.Mui-focused fieldset": { borderColor: DL.green },
  },
  "& .MuiInputBase-input": { color: DL.textPrimary, fontSize: "0.9rem" },
  "& .MuiInputLabel-root": { color: DL.textMuted, fontSize: "0.9rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: DL.green },
  "& .MuiFormHelperText-root": { color: DL.red },
};

const EditPhotos = () => {
  const { user, isLoggedIn, setUser } = useContext(AuthContext);
  const { isMobile } = useContext(DeviceContext);
  const [searchParams] = useSearchParams();
  const isOAuthSignup = searchParams.get("oauthSignup") === "true";

  const [avatarFile, setAvatarFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(
    user && user.avatarUrl != null ? getImageUrl(user.avatarUrl) : null,
  );
  const [bannerUrl, setBannerUrl] = useState(
    user && user.bannerUrl != null ? getImageUrl(user.bannerUrl) : null,
  );
  const [croppedAvatar, setCroppedAvatar] = useState(null);
  const [croppedBanner, setCroppedBanner] = useState(null);
  const [avatarCrop, setAvatarCrop] = useState({ x: 0, y: 0 });
  const [avatarZoom, setAvatarZoom] = useState(1);
  const [bannerCrop, setBannerCrop] = useState({ x: 0, y: 0 });
  const [bannerZoom, setBannerZoom] = useState(1);
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [avatarInputName, setAvatarInputName] = useState("");
  const [bannerInputName, setBannerInputName] = useState("");
  const [avatarChanged, setAvatarChanged] = useState(false);
  const [bannerChanged, setBannerChanged] = useState(false);

  const [newUsername, setNewUsername] = useState(user?.username || "");
  const [usernameError, setUsernameError] = useState("");
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) navigate("/signin");
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    if (!isOAuthSignup || !newUsername || newUsername === user?.username) {
      setUsernameAvailable(null);
      return;
    }
    const checkUsername = async () => {
      if (newUsername.length < 3 || newUsername.length > 15) {
        setUsernameError("Username must be 3-15 characters");
        setUsernameAvailable(false);
        return;
      }
      if (!/^[a-zA-Z0-9_]+$/.test(newUsername)) {
        setUsernameError("Only letters, numbers, and underscores allowed");
        setUsernameAvailable(false);
        return;
      }
      setCheckingUsername(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SC_API_BASE_URL}/api/auth/check-username?username=${newUsername}`,
        );
        setUsernameAvailable(response.data.available);
        setUsernameError(response.data.available ? "" : "Username is taken");
      } catch (error) {
        console.error("Error checking username:", error);
        setUsernameError("");
        setUsernameAvailable(null);
      } finally {
        setCheckingUsername(false);
      }
    };
    const debounce = setTimeout(checkUsername, 500);
    return () => clearTimeout(debounce);
  }, [newUsername, isOAuthSignup, user?.username]);

  const usernameConditions = [
    {
      label: "3 to 15 characters",
      isValid: newUsername.length >= 3 && newUsername.length <= 15,
    },
    {
      label: "Only letters, numbers, and underscores",
      isValid: /^[a-zA-Z0-9_]+$/.test(newUsername),
    },
    { label: "No spaces", isValid: !/\s/.test(newUsername) },
  ];

  const steps = isOAuthSignup
    ? ["Username", "Avatar", "Banner", "Review"]
    : ["Avatar", "Banner", "Review"];

  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    setAvatarFile(file);
    setAvatarInputName(file.name);
    setAvatarUrl(URL.createObjectURL(file));
    setAvatarChanged(true);
  };

  const handleBannerChange = (event) => {
    const file = event.target.files[0];
    setBannerFile(file);
    setBannerInputName(file.name);
    setBannerUrl(URL.createObjectURL(file));
    setBannerChanged(true);
  };

  const handleAvatarCropComplete = (croppedImage) => {
    setCroppedAvatar(croppedImage);
    setAvatarChanged(true);
  };
  const handleBannerCropComplete = (croppedImage) => {
    setCroppedBanner(croppedImage);
    setBannerChanged(true);
  };
  const handleAvatarCropChange = (crop) => setAvatarCrop(crop);
  const handleAvatarZoomChange = (zoom) => setAvatarZoom(zoom);
  const handleBannerCropChange = (crop) => setBannerCrop(crop);
  const handleBannerZoomChange = (zoom) => setBannerZoom(zoom);

  const handleNext = async () => {
    if (isOAuthSignup && activeStep === 0) {
      if (!usernameAvailable && newUsername !== user?.username) {
        setUsernameError("Please choose an available username");
        return;
      }
      if (newUsername !== user?.username) {
        setLoading(true);
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_SC_API_BASE_URL}/api/auth/update-username`,
            { newUsername },
            { headers: { Authorization: `Bearer ${user.token}` } },
          );
          if (response.status === 200) {
            setUser({
              ...user,
              username: newUsername,
              token: response.data.token,
            });
          }
        } catch (error) {
          console.error("Error updating username:", error);
          setUsernameError(
            error.response?.data?.message || "Failed to update username",
          );
          setLoading(false);
          return;
        } finally {
          setLoading(false);
        }
      }
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);


  const handleSave = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      if (avatarChanged && croppedAvatar) {
        const avatarBlob = await fetch(croppedAvatar).then((res) => res.blob());
        formData.append(
          "avatar",
          new File([avatarBlob], avatarInputName, { type: avatarBlob.type }),
        );
      }
      if (bannerChanged && croppedBanner) {
        const bannerBlob = await fetch(croppedBanner).then((res) => res.blob());
        formData.append(
          "banner",
          new File([bannerBlob], bannerInputName, { type: bannerBlob.type }),
        );
      }
      const response = await axios.post(
        `${process.env.REACT_APP_SC_API_BASE_URL}/api/user/updatePhotos`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      const updatedUser = { ...user };
      if (response.data.avatarUrl)
        updatedUser.avatarUrl = response.data.avatarUrl;
      if (response.data.bannerUrl)
        updatedUser.bannerUrl = response.data.bannerUrl;
      setUser(updatedUser);
      if (location.pathname === "/edit-photos")
        navigate(`/profile/${user.username}`);
      else if (location.pathname === "/add-new-photos")
        navigate(`/add-user-profile`);
    } catch (error) {
      console.error("Error updating photos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAvatar = () => {
    setAvatarFile(null);
    setAvatarUrl(null);
    setCroppedAvatar(null);
    setAvatarInputName("");
    setAvatarChanged(false);
  };

  const handleDeleteBanner = () => {
    setBannerFile(null);
    setBannerUrl(null);
    setCroppedBanner(null);
    setBannerInputName("");
    setBannerChanged(false);
  };

  const getStepContent = (step) => {
    const adjustedStep = isOAuthSignup ? step : step + 1;
    switch (adjustedStep) {
      case 0:
        return (
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.95rem",
                color: DL.green,
                mb: 0.5,
              }}>
              Choose Username
            </Typography>
            <Typography
              sx={{ fontSize: "0.875rem", color: DL.textSecondary, mb: 2 }}>
              Choose a short, recognizable handle.
            </Typography>
            <TextField
              label="Username"
              variant="outlined"
              fullWidth
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              error={!!usernameError}
              helperText={usernameError}
              InputProps={{
                endAdornment: checkingUsername ? (
                  <CircularProgress size={18} sx={{ color: DL.green }} />
                ) : usernameAvailable === true ? (
                  <CheckIcon sx={{ color: DL.green, fontSize: 20 }} />
                ) : usernameAvailable === false ? (
                  <CloseIcon sx={{ color: DL.red, fontSize: 20 }} />
                ) : null,
              }}
              sx={{ ...inputSx, maxWidth: isMobile ? "100%" : 420 }}
            />
            <Box sx={{ mt: 2, maxWidth: 420 }}>
              {usernameConditions.map((c, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mb: 0.5,
                  }}>
                  {c.isValid ? (
                    <CheckIcon sx={{ fontSize: 14, color: DL.green }} />
                  ) : (
                    <CloseIcon sx={{ fontSize: 14, color: DL.red }} />
                  )}
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      color: c.isValid ? DL.green : DL.red,
                    }}>
                    {c.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.95rem",
                color: DL.green,
                mb: 0.5,
                textAlign: "center",
              }}>
              Profile Photo
            </Typography>
            <Typography
              sx={{
                fontSize: "0.875rem",
                color: DL.textSecondary,
                mb: 2,
                textAlign: "center",
              }}>
              Resize your avatar
            </Typography>
            <input
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              style={{ display: "none" }}
              id="avatar-upload"
            />
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <label htmlFor="avatar-upload">
                <Button
                  component="span"
                  sx={{
                    background: `linear-gradient(to right, #10b981, #059669)`,
                    color: "#fff",
                    borderRadius: "8px",
                    px: 2.5,
                    py: 1,
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    textTransform: "none",
                    boxShadow: "0px 2px 8px rgba(98,244,129,0.3)",
                    "&:hover": { filter: "brightness(110%)" },
                  }}>
                  Choose Image
                </Button>
              </label>
            </Box>
            {avatarInputName && (
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: DL.textMuted,
                  textAlign: "center",
                  mb: 1.5,
                }}>
                {avatarInputName}
              </Typography>
            )}
            {avatarUrl ? (
              <Box
                sx={{
                  position: "relative",
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: `1px solid ${DL.borderGreen}`,
                }}>
                <ImageCropper
                  imageSrc={avatarUrl}
                  onCropComplete={handleAvatarCropComplete}
                  cropShape="round"
                  aspect={1}
                  crop={avatarCrop}
                  zoom={avatarZoom}
                  onCropChange={handleAvatarCropChange}
                  onZoomChange={handleAvatarZoomChange}
                />
                <IconButton
                  onClick={handleDeleteAvatar}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "rgba(255,100,103,0.85)",
                    "&:hover": { backgroundColor: DL.red },
                    color: "#fff",
                  }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ) : (
              <Box
                sx={{
                  border: `1px dashed ${DL.borderGreen}`,
                  borderRadius: "12px",
                  height: isMobile ? "12rem" : "20rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: DL.textMuted,
                  fontSize: "0.875rem",
                }}>
                Select an image to begin
              </Box>
            )}
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.95rem",
                color: DL.green,
                mb: 0.5,
                textAlign: "center",
              }}>
              Profile Banner
            </Typography>
            <Typography
              sx={{
                fontSize: "0.875rem",
                color: DL.textSecondary,
                mb: 2,
                textAlign: "center",
              }}>
              Resize your image
            </Typography>
            <input
              type="file"
              accept="image/*"
              onChange={handleBannerChange}
              style={{ display: "none" }}
              id="banner-upload"
            />
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <label htmlFor="banner-upload">
                <Button
                  component="span"
                  sx={{
                    background: `linear-gradient(to right, #10b981, #059669)`,
                    color: "#fff",
                    borderRadius: "8px",
                    px: 2.5,
                    py: 1,
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    textTransform: "none",
                    boxShadow: "0px 2px 8px rgba(98,244,129,0.3)",
                    "&:hover": { filter: "brightness(110%)" },
                  }}>
                  Choose Image
                </Button>
              </label>
            </Box>
            {bannerInputName && (
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: DL.textMuted,
                  textAlign: "center",
                  mb: 1.5,
                }}>
                {bannerInputName}
              </Typography>
            )}
            {bannerUrl ? (
              <Box
                sx={{
                  position: "relative",
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: `1px solid ${DL.borderGreen}`,
                }}>
                <ImageCropper
                  imageSrc={bannerUrl}
                  onCropComplete={handleBannerCropComplete}
                  cropShape="rect"
                  aspect={16 / 9}
                  crop={bannerCrop}
                  zoom={bannerZoom}
                  onCropChange={handleBannerCropChange}
                  onZoomChange={handleBannerZoomChange}
                  cropAreaHeight={isMobile ? 120 : 160}
                  fillCropArea
                />
                <IconButton
                  onClick={handleDeleteBanner}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "rgba(255,100,103,0.85)",
                    "&:hover": { backgroundColor: DL.red },
                    color: "#fff",
                  }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ) : (
              <Box
                sx={{
                  border: `1px dashed ${DL.borderGreen}`,
                  borderRadius: "12px",
                  height: isMobile ? 120 : 160,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: DL.textMuted,
                  fontSize: "0.875rem",
                }}>
                Select an image to begin
              </Box>
            )}
          </Box>
        );
      case 3:
        return (
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.95rem",
                color: DL.green,
                mb: 2,
                textAlign: "center",
              }}>
              Preview
            </Typography>
            <Box
              sx={{
                maxWidth: 560,
                mx: "auto",
                borderRadius: "12px",
                overflow: "hidden",
                border: `1px solid ${DL.border}`,
                backgroundColor: DL.bgLight,
              }}>
              {/* Banner preview */}
              <Box
                sx={{
                  height: isMobile ? 120 : 160,
                  background: croppedBanner
                    ? `url(${typeof croppedBanner === "string" ? croppedBanner : URL.createObjectURL(croppedBanner)}) center/cover no-repeat`
                    : `linear-gradient(135deg, rgba(16,56,32,0.5) 0%, rgba(6,30,20,0.5) 100%)`,
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(to top, #0f1419 0%, transparent 60%)",
                  },
                }}>
                {croppedBanner && (
                  <IconButton
                    onClick={handleDeleteBanner}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      backgroundColor: "rgba(255,100,103,0.85)",
                      "&:hover": { backgroundColor: DL.red },
                      color: "#fff",
                    }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
              {/* Avatar + username */}
              <Box sx={{ px: 2.5, pb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                    mt: isMobile ? "-36px" : "-48px",
                  }}>
                  <Box sx={{ position: "relative" }}>
                    <Avatar
                      alt={user?.username}
                      src={
                        croppedAvatar
                          ? typeof croppedAvatar === "string"
                            ? croppedAvatar
                            : URL.createObjectURL(croppedAvatar)
                          : undefined
                      }
                      sx={{
                        width: isMobile ? 72 : 96,
                        height: isMobile ? 72 : 96,
                        border: `4px solid ${DL.bgLight}`,
                        boxShadow: "0px 4px 12px rgba(98,244,129,0.3)",
                        bgcolor: `hsl(${(user?.username?.charCodeAt(0) * 20) % 360}, 40%, 35%)`,
                        fontSize: isMobile ? "1.5rem" : "2rem",
                        fontWeight: 700,
                      }}>
                      {user?.username?.charAt(0).toUpperCase()}
                    </Avatar>
                    {croppedAvatar && (
                      <IconButton
                        onClick={handleDeleteAvatar}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          backgroundColor: "rgba(255,100,103,0.85)",
                          "&:hover": { backgroundColor: DL.red },
                          color: "#fff",
                          width: 20,
                          height: 20,
                        }}>
                        <CloseIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    color: DL.textPrimary,
                    mt: 1,
                  }}>
                  {user?.username}
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", color: DL.textMuted }}>
                  This is how your profile will look.
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  const isLastStep = activeStep === steps.length - 1;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: DL.bgDark,
        background: `radial-gradient(ellipse at 50% 0%, rgba(98,244,129,0.05) 0%, ${DL.bgDark} 55%)`,
        px: 2,
        py: isMobile ? 3 : 5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}>
      <ResponsiveTopBar isMobile={isMobile} onMobileMenuToggle={() => setMobileMenuOpen((prev) => !prev)} />
      <MobileNavDrawer open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {/* Card */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 620,
          mt: `64px`,
          backgroundColor: DL.bgMedium,
          border: `1px solid ${DL.border}`,
          borderRadius: "12px",
          boxShadow: "0 4px 40px rgba(0,0,0,0.4)",
          overflow: "hidden",
        }}>
        {/* Header */}
        <Box
          sx={{
            px: isMobile ? 3 : 4,
            py: 3,
            borderBottom: `1px solid ${DL.divider}`,
          }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: isMobile ? "1.3rem" : "1.6rem",
              color: DL.textPrimary,
            }}>
            {location.pathname === "/add-new-photos"
              ? "Set Up Your Profile"
              : "Edit Profile Media"}
          </Typography>
          <Typography
            sx={{ fontSize: "0.8rem", color: DL.textMuted, mt: 0.25 }}>
            Step {activeStep + 1} of {steps.length}: {steps[activeStep]}
          </Typography>
        </Box>

        {/* Step dots */}
        <Box sx={{ px: isMobile ? 3 : 4, pt: 2.5, display: "flex", gap: 1 }}>
          {steps.map((label, i) => (
            <Box
              key={label}
              sx={{
                height: 4,
                flex: 1,
                borderRadius: 999,
                backgroundColor:
                  i <= activeStep ? DL.green : "rgba(255,255,255,0.22)",
                transition: "background-color 0.3s ease",
              }}
            />
          ))}
        </Box>

        {/* Step content */}
        <Box sx={{ px: isMobile ? 3 : 4, pt: 3, pb: 1 }}>
          {getStepContent(activeStep)}
        </Box>

        {/* Actions */}
        <Box
          sx={{
            px: isMobile ? 3 : 4,
            py: 3,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}>
          <Stack direction="row" justifyContent="space-between" gap={1}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{
                backgroundColor: DL.bgLight,
                border: `1px solid ${DL.textMuted}`,
                color: DL.textPrimary,
                borderRadius: "8px",
                px: 2.5,
                py: 1,
                fontWeight: 600,
                fontSize: "0.875rem",
                textTransform: "none",
                "&:hover": {
                  borderColor: DL.textSecondary,
                  backgroundColor: "rgba(255,255,255,0.06)",
                },
                "&:disabled": { opacity: 0.9 },
                transition: "all 0.2s ease",
              }}>
              Back
            </Button>
            {isLastStep ? (
              <Button
                onClick={handleSave}
                disabled={loading}
                sx={{
                  background: `linear-gradient(to right, #10b981, #059669)`,
                  color: "#fff",
                  borderRadius: "8px",
                  px: 3,
                  py: 1,
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  textTransform: "none",
                  boxShadow: "0px 2px 8px rgba(98,244,129,0.4)",
                  "&:hover": { filter: "brightness(110%)" },
                  "&:disabled": { opacity: 0.5 },
                  minWidth: 130,
                  transition: "all 0.2s ease",
                }}>
                {loading ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Save & Finish"
                )}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={loading}
                sx={{
                  background: `linear-gradient(to right, #10b981, #059669)`,
                  color: "#fff",
                  borderRadius: "8px",
                  px: 3,
                  py: 1,
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  textTransform: "none",
                  boxShadow: "0px 2px 8px rgba(98,244,129,0.4)",
                  "&:hover": { filter: "brightness(110%)" },
                  "&:disabled": { opacity: 0.5 },
                  transition: "all 0.2s ease",
                }}>
                {loading ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Continue"
                )}
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default EditPhotos;
